//@ts-nocheck

import { Splide, SplideSlide } from "@splidejs/react-splide";
import { Options } from "@splidejs/splide";
import React, { useContext, useState, useRef } from "react";
import { Button } from "../Button/Button";
import { Icon } from "../Icon";
import CDNImage from "../Image/CDNImage";
import ReactPanZoom from "./lib/ReactPanZoom";
import * as gtag from "@/lib/gtag";
import { PageContext } from "pages/[[...slug]]";
import { isMobileOrTablet } from "@/lib/isMobileOrTablet";
import ButtonPhone from "@/components/ButtonPhone/ButtonPhone";
import { useIsomorphicLayoutEffect } from "hooks/useIsomorphicLayoutEffect";

const ThumbnailSlider = ({ assets, start = 1, nonBindingRequestLink }) => {
  const [currentActive, setCurrentActive] = useState(start || 1);
  const { config } = useContext(PageContext);

  const mainRef = useRef<any>(null);
  const thumbsRef = useRef<any>(null);
  const fullscreenRef = useRef<any>(null);
  const mainContainerRef = useRef<any>(null);

  const supportsFullscreen = !!document.documentElement.requestFullscreen;
  const [isFullscreen, setIsFullscreen] = useState(false);
  function handleFullscreenchange() {
    setReset(true);
    setIsFullscreen(!!document.fullscreenElement);
  }
  useIsomorphicLayoutEffect(() => {
    document.addEventListener("fullscreenchange", handleFullscreenchange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenchange);
    };
  }, []);
  function toggleFullscreen() {
    const elem = mainContainerRef.current;
    if (!elem?.requestFullscreen) return;

    if (!document.fullscreenElement) {
      elem.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  /**
   * Set the sync target right after the component is mounted.
   */
  useIsomorphicLayoutEffect(() => {
    if (mainRef.current) {
      if (thumbsRef.current?.splide) {
        mainRef.current.sync(thumbsRef.current.splide);
      }

      if (fullscreenRef.current?.splide) {
        mainRef.current.sync(fullscreenRef.current.splide);
      }
    }
  }, [start]);

  const [isMobile, setIsMobile] = useState(false);
  useIsomorphicLayoutEffect(() => {
    setIsMobile(isMobileOrTablet());
  }, []);

  const mainOptions: Options = {
    type: "loop",
    paginationKeyboard: true,
    keyboard: true,
    perPage: 1,
    perMove: 1,
    gap: "1rem",
    pagination: false,
    start: start || 1,
    drag: isMobile,
    arrows: true,
    classes: {
      arrows: "splide__arrows w-full relative z-10",
      arrow:
        "splide__arrow absolute top-[100px] lg:top-[26rem]   2xl:top-[22rem] hd:top-[400px] fill-orange-medium z-50 p-4 hover:!bg-blue-dark bg-[#1b394e4a] rounded-full [&>svg]:h-7 [&>svg]:w-7",
      prev: "splide__arrow--prev left-4  lg:left-[-70px] rotate-180",
      next: "splide__arrow--next right-4 lg:right-[-70px]",
    },
  };

  const thumbsOptions: Options = {
    type: "slide",
    paginationKeyboard: true,
    keyboard: true,
    rewind: true,
    fixedWidth: 122,
    fixedHeight: 95,
    pagination: false,
    arrows: true,
    cover: true,
    focus: "center",
    isNavigation: true,
    start: start,
    classes: {
      arrows: "splide__arrows w-full relative z-10",
      arrow: "splide__arrow absolute top-[25px]",
      prev: "splide__arrow--prev left-[-10px] rotate-180  fill-orange-medium [&>svg]:h-7 [&>svg]:w-7 [&>svg]:-ml-4 p-4 bg-[#1b394e4a] rounded-full",
      next: "splide__arrow--next right-[-10px]  fill-orange-medium [&>svg]:h-7 [&>svg]:w-7 [&>svg]:-ml-4 p-4 bg-[#1b394e4a] rounded-full",
    },
  };

  const fullscreenOptions: Options = {
    ...mainOptions,
    arrows: true,
    classes: {
      ...mainOptions.classes,
      prev: "splide__arrow--prev left-4  top-[50vh] rotate-180",
      next: "splide__arrow--next right-4 top-[50vh]",
    },
  };

  const [reset, setReset] = useState(false);
  const handleOnMove = (e) => {
    setCurrentActive(e.index);
    setReset(true);
  };

  return (
    <div className="max-h-[55vh] hd:max-w-[95%] lg:max-w-[100%] uhd:max-w-none w-auto mx-auto relative">
      <div ref={mainContainerRef} className="relative h-full w-full">
        <div className="absolute bottom-6 left-6 py-1 px-4 bg-gray-600 text-white text-sm z-20 rounded-6 opacity-60 select-none">
          {currentActive + 1} / {assets.images.length}
        </div>
        {supportsFullscreen ? (
          <>
            <button
              className="absolute top-6 right-6 py-1 px-4 bg-gray-600 text-white text-sm z-20 rounded-6 opacity-60 pointer"
              onClick={toggleFullscreen}
            >
              <span className="flex row items-center">
                {isFullscreen ? (
                  <>
                    <Icon type="chevron" className="rotate-180 w-5 h-5 mr-4" />
                    zurück
                  </>
                ) : (
                  <>
                    <Icon type="zoom" className="w-6 h-6 mr-4" />
                    vergrößern
                  </>
                )}
              </span>
            </button>
            <div
              className={`
                relative w-full h-full
                ${isFullscreen ? "" : "hidden"}
              `}
            >
              <ReactPanZoom
                image={assets.images[currentActive]}
                alt=""
                reset={reset}
                setReset={setReset}
                className="overflow-hidden absolute top-0 left-0 w-full h-full flex justify-center"
              />
              <Splide
                ref={fullscreenRef}
                options={fullscreenOptions}
                onMoved={(e) => handleOnMove(e)}
              >
                {/* empty slide to use the arrows as custom fullscreen-arrows */}
                {assets.images.map((_image, index) => (
                  <SplideSlide key={index}></SplideSlide>
                ))}
              </Splide>
            </div>
          </>
        ) : (
          <>
            <ReactPanZoom
              image={assets.images[currentActive]}
              alt=""
              reset={reset}
              setReset={setReset}
              className="overflow-hidden absolute top-0 left-0 w-full h-full flex justify-center"
            />
          </>
        )}
        <Splide
          ref={mainRef}
          options={mainOptions}
          onMoved={(e) => handleOnMove(e)}
        >
          {assets.images.map((image, index) => (
            <SplideSlide key={index}>
              <div className="relative w-full h-full">
                <CDNImage
                  src={image}
                  width="1000"
                  quality={65}
                  height="760"
                  alt=""
                  priority={index === start}
                  className={`2xl:w-full 2xl:h-full ${
                    supportsFullscreen
                      ? "cursor-pointer"
                      : "opacity-100 lg:opacity-0"
                  }`}
                  sizes={"(max-width: 1023px) 100vw, 50vw"}
                  onClick={() => supportsFullscreen && toggleFullscreen()}
                />
              </div>
            </SplideSlide>
          ))}
        </Splide>
      </div>

      <Splide
        ref={thumbsRef}
        options={thumbsOptions}
        onMove={(e) => handleOnMove(e)}
        className="overflow-hidden hidden lg:block"
        aria-label="The carousel with thumbnails. Selecting a thumbnail will change the main carousel"
      >
        {assets.images.map((image, index) => {
          const activeIndex = start || 0;
          const isPriority = !!(
            index >= activeIndex - 4 && index <= activeIndex + 4
          );

          return (
            <SplideSlide
              key={index}
              className={`is-active:border-blue-light border-transparent is-active:border-solid is-active:border-4 hover:border-4 hover:border-gray-300 `}
            >
              <CDNImage
                src={image}
                width="60"
                height="30"
                quality={80}
                alt=""
                priority={isPriority}
              />
            </SplideSlide>
          );
        })}
      </Splide>
      <div className="flex lg:hidden lg:flex-col gap-6 lg:mt-0 mt-7 mx-4 ">
        <Button
          size="medium"
          as="a"
          href={nonBindingRequestLink}
          className="cursor-pointer  lg:mt-6 !lg:text-base !text-sm w-full !px-0"
          onClick={() => {
            gtag.event({
              event: "itemClick",
              itemClick: "Request",
              itemPosition: "picturesGalery",
            });
          }}
        >
          <Icon type="mail" className="w-6 h-6 lg:mr-4 mr-4" />
          Kauf anfragen
        </Button>
        <ButtonPhone
          className="!lg:text-base !text-sm whitespace-nowrap !px-0 w-full"
          size="medium"
          config={config}
          itemPosition="picturesGalery"
        />
      </div>
    </div>
  );
};

export default ThumbnailSlider;
